interface SparkProps {
  /** The trend series (oldest → newest). */
  values: number[];
  /** Concrete stroke/fill color (the caller picks it to match the tile tone). */
  color: string;
  height?: number;
  width?: number;
}

/**
 * Tiny inline trend line for a {@link StatCard} — a filled area under a 1.75px
 * line with a terminal dot. Renders nothing below two points. Port of the
 * prototype `Sparkline`. Decorative, so `aria-hidden`.
 */
export function Sparkline({ values, color, height = 44, width = 120 }: SparkProps) {
  if (!values || values.length < 2) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const padY = 4;
  const innerH = height - padY * 2;
  const stepX = width / (values.length - 1);
  const points = values.map(
    (v, i) => [i * stepX, padY + innerH - ((v - min) / range) * innerH] as const,
  );
  const line = points
    .map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`)
    .join(' ');
  const areaPath = `${line} L ${width} ${height} L 0 ${height} Z`;
  const last = points[points.length - 1]!;

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
      <path d={areaPath} fill={color} opacity="0.15" />
      <path
        d={line}
        fill="none"
        stroke={color}
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx={last[0]} cy={last[1]} r="2.5" fill={color} />
    </svg>
  );
}

/**
 * Bar-chart variant of {@link Sparkline}: one bottom-aligned bar per value with
 * a stronger terminal bar to call out the current period. Port of the prototype
 * `SparkBars`.
 */
export function SparkBars({ values, color, height = 44, width = 120 }: SparkProps) {
  if (!values || values.length === 0) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const floor = Math.max(0, min - range * 0.2);
  const gap = 1.5;
  const barW = Math.max(2, (width - (values.length - 1) * gap) / values.length);

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
      {values.map((v, i) => {
        const h = ((v - floor) / (max - floor || 1)) * (height - 4);
        const x = i * (barW + gap);
        const y = height - h - 1;
        const isLast = i === values.length - 1;
        return (
          <rect
            key={i}
            x={x}
            y={y}
            width={barW}
            height={Math.max(1.5, h)}
            fill={color}
            opacity={isLast ? 1 : 0.55}
            rx="1"
          />
        );
      })}
    </svg>
  );
}
